/**
 * React Query hooks for CRM email templates.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { CrmCreateEmailTemplateInput, CrmEmailTemplate } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseEmailTemplatesOptions {
  search?: string;
  limit?: number;
}

function buildParams(options: UseEmailTemplatesOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.search) params.search = options.search;
  if (options.limit !== undefined) params.limit = options.limit;
  return params;
}

export function useEmailTemplates(options: UseEmailTemplatesOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<{ templates: CrmEmailTemplate[]; total: number }>({
    queryKey: queryKeys.crm.emailTemplates.list(filters),
    queryFn: () => api.listCrmEmailTemplates(Object.keys(filters).length > 0 ? filters : undefined),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.crm.emailTemplates.all() });

  const createMutation = useMutation({
    mutationFn: (input: CrmCreateEmailTemplateInput) => api.createCrmEmailTemplate(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create template', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CrmCreateEmailTemplateInput> }) =>
      api.updateCrmEmailTemplate(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update template', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCrmEmailTemplate(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete template', variant: 'destructive' }),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load email templates',
      variant: 'destructive',
    });
  }

  return {
    templates: query.data?.templates ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createTemplate: (input: CrmCreateEmailTemplateInput) => createMutation.mutateAsync(input),
    updateTemplate: (id: string, input: Partial<CrmCreateEmailTemplateInput>) =>
      updateMutation.mutateAsync({ id, input }),
    deleteTemplate: (id: string) => deleteMutation.mutateAsync(id),
    previewTemplate: (id: string, variables: Record<string, string>) =>
      api.previewCrmEmailTemplate(id, variables),
  };
}
